import { Observable, forkJoin, take, tap } from 'rxjs';
import { Type } from '@angular/core';

import { AppComponent } from './app.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { LoginComponent } from './components/authentication/login/login.component';
import { StyleGuideComponent } from './components/styleguide/styleguide.component';

import { TranslateService } from '@ngx-translate/core';
import { ForgotUserNameComponent } from './components/authentication/forgotusername/forgotusername.component';
import { CredentialsComponent } from './components/authentication/credentials/credentials.component';
import { CreateAccountComponent } from './components/authentication/createaccount/createaccount.component';
import { VerificationComponent } from './components/authentication/verification/verification.component';
import { UnauthorizedComponent } from './components/navigation-sidebar/unauthorized/unauthorized.component';

import { ChangePasswordComponent } from './components/navigation-sidebar/my-account/change-password/change-password.component';

import {
  RequiredValidator, CheckClashValidator, ExactMatchValidator, UrlValidator, ZipValidatorDirective, NoLeadingTrailingSpacesValidator,
  NoSpecialCharValidatorDirective, FirstNameValidatorDirective, LastNameValidatorDirective, EmailValidatorDirective,
  AlphaNumOnlyDirective, ShortCodeValidatorDirective, ShortCodeInputDirective, CvvValidator, CcValidator, CcExpirationValidator, FecIdValidator, GreaterComparisonValidator, LessComparisonValidator, CheckSumValidator, AmountValidator, RangeValidator, LeiChecksumValidator,
  PublicDomainEmailValidatorDirective
} from './directives/validators.directive';
import { ScrEmailDirective, ScrPasswordDirective, ScrTextDirective, ZipCodeDirective, UpperCaseDirective, NumericOnlyDirective, MustMatchDirective, ScrTestIdDirective, ScrDropdownDirective, ScrInputNumberDirective, ScrMultiSelectDirective } from './directives/forms.directive';
import { IuiMessagesComponent } from './shared/messages/messages.component';
import { IuiBusyComponent } from './shared/busy/busy.component';
import { GenericConfirmComponent } from './shared/confirm/confirm.component';
import { IntendedUsageComponent } from './shared/intended-usage/intended-usage.component';
import { ChipsDirective } from './components/forms/chips';
import { CreditCardPipe } from './shared/credit-card.pipe';
import { CreditCardDirective } from './directives/credit-card.directive';

import { AutoRenewPipe, LeaseStatusPipe, LeaseTypePipe, OrderStatusPipe, PaymentMethodAccountTypePipe, TermLengthPipe, TransferTypePipe, YesNoPipe, OfferTypePipe, TrueFalsePipe, DiscountSummaryPipe, StatusReasonPipe, LeaseStatusReasonPipe, OrderTypePipe, SearchPipe } from './shared/table-data.pipe';
import { AuthIfCombinedDirective, AuthIfDirective, NotAuthIfDirective } from './directives/authif.directive';
import { InlineMessageComponent } from './components/forms/inline-message/inline-message.component';
import { MbOptOutComponent } from './shared/mb-opt-out/mb-opt-out.component';
import { MbOptOutSuccessComponent } from './shared/mb-opt-out-success/mb-opt-out-success.component';

import { IndustryMonReportPipe, ScrDatePipe, ScrDatePendingPipe, ScrDateTimePipe } from './shared/scr-date.pipe';
import { ScrTimePipe } from './shared/scr-time.pipe';
import { ScrMoneyPipe } from './shared/scr-money.pipe';
import { CountryDropdownComponent } from './components/forms/country-dropdown/country-dropdown.component';
import { RegionDropdownComponent } from './components/forms/region-dropdown/region-dropdown.component';
import { ZipCodeComponent } from './components/forms/zipcode/zipcode.component';
import { ConfigPipe } from './shared/config.pipe';
import { BannerComponent } from './components/banner/banner.component';
import { CreditCardFormatDirective } from './directives/credit-card-format.directive';
import { DynamicTabpaneComponent } from './components/custom-controls/dynamic-tabpane/dynamic-tabpane.component';
import { DynamicDialogComponent } from './components/custom-controls/dynamic-dialog/dynamic-dialog.component';
import { ScrInactivityComponent } from './components/inactivity/inactivity.component';
import { AddressBookComponent } from './components/forms/address-book/address-book.component';
import { HelpLinkIconComponent } from './components/forms/help-link-icon/help-link-icon.component';
import { TruncatedDirective } from './directives/truncate-tooltip.directive';
import { PreventDoubleClickDirective } from './directives/events.directive';
import { PhoneNumberPipe } from './shared/phone.pipe';
import { PhoneComponent } from './components/custom-controls/phone-component';
import { LinkedAccountsHeaderComponent } from './components/navigation-sidebar/linked-accounts/linked-accounts-header/linked-accounts-header.component';
import { VetResultsDialogComponent } from './shared/vet-results-dialog/vet-results-dialog.component';

import { IdTokenDialogComponent } from './shared/id-token-dialog/id-token-dialog.component';
import { UtilitiesDialogComponent } from './shared/utilities-dialog/utilities-dialog.component';
import { NewVersionDialogComponent } from './shared/new-version-dialog/new-version-dialog.component';

import { ConfigService } from './services';


/**
 * PLEASE NOTICE THE SORTING OF THE ENTRIES BELOW.
 */

import { AccordionModule } from 'primeng/accordion';
import { BadgeModule } from 'primeng/badge';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipsModule } from 'primeng/chips';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ListboxModule } from 'primeng/listbox';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ScrEnvironment } from '../environments/environment';
import { NGXLogger, NgxLoggerLevel } from 'ngx-logger';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validators: Type<any>[] = [
  AmountValidator,
  CcExpirationValidator,
  CcValidator,
  CheckClashValidator,
  CheckSumValidator,
  CreditCardFormatDirective,
  CvvValidator,
  EmailValidatorDirective,
  ExactMatchValidator,
  FecIdValidator,
  FirstNameValidatorDirective,
  GreaterComparisonValidator,
  LastNameValidatorDirective,
  LeiChecksumValidator,
  LessComparisonValidator,
  NoLeadingTrailingSpacesValidator,
  NoSpecialCharValidatorDirective,
  PublicDomainEmailValidatorDirective,
  RangeValidator,
  RequiredValidator,
  ShortCodeValidatorDirective,
  UrlValidator,
  ZipValidatorDirective
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const pipes: Type<any>[] = [
  AutoRenewPipe,
  ConfigPipe,
  CreditCardPipe,
  DiscountSummaryPipe,
  SearchPipe,
  IndustryMonReportPipe,
  LeaseStatusPipe,
  LeaseStatusReasonPipe,
  LeaseTypePipe,
  OfferTypePipe,
  OrderStatusPipe,
  OrderTypePipe,
  PaymentMethodAccountTypePipe,
  PhoneNumberPipe,
  ScrDatePendingPipe,
  ScrDatePipe,
  ScrDateTimePipe,
  ScrMoneyPipe,
  ScrTimePipe,
  StatusReasonPipe,
  TermLengthPipe,
  TransferTypePipe,
  TrueFalsePipe,
  YesNoPipe
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const directives: Type<any>[] = [
  AlphaNumOnlyDirective,
  AuthIfCombinedDirective,
  AuthIfDirective,
  ChipsDirective,
  CreditCardDirective,
  MustMatchDirective,
  NotAuthIfDirective,
  NumericOnlyDirective,
  PreventDoubleClickDirective,
  ScrEmailDirective,
  ScrPasswordDirective,
  ScrTextDirective,
  ScrInputNumberDirective,
  ScrDropdownDirective,
  ScrMultiSelectDirective,
  ScrTestIdDirective,
  ShortCodeInputDirective,
  TruncatedDirective,
  UpperCaseDirective,
  ZipCodeDirective
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sharedcomponents: Type<any>[] = [
  AddressBookComponent,
  AppComponent,
  BannerComponent,
  ChangePasswordComponent,
  CountryDropdownComponent,
  CreateAccountComponent,
  CredentialsComponent,
  DynamicTabpaneComponent,
  DynamicDialogComponent,
  FooterComponent,
  ForgotUserNameComponent,
  GenericConfirmComponent,
  HeaderComponent,
  HelpLinkIconComponent,
  IdTokenDialogComponent,
  InlineMessageComponent,
  IntendedUsageComponent,
  IuiBusyComponent,
  IuiMessagesComponent,
  LinkedAccountsHeaderComponent,
  LoginComponent,
  MbOptOutComponent,
  MbOptOutSuccessComponent,
  PhoneComponent,
  RegionDropdownComponent,
  ScrInactivityComponent,
  StyleGuideComponent,
  UnauthorizedComponent,
  UtilitiesDialogComponent,
  NewVersionDialogComponent,
  VerificationComponent,
  VetResultsDialogComponent,
  ZipCodeComponent
];


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sharedPrimeNgModules: Type<any>[] = [
  AccordionModule,
  BadgeModule,
  CalendarModule,
  CheckboxModule,
  ChipsModule,
  ConfirmDialogModule,
  DialogModule,
  DropdownModule,
  InputNumberModule,
  InputSwitchModule,
  ListboxModule,
  MessageModule,
  MessagesModule,
  MultiSelectModule,
  PaginatorModule,
  PanelModule,
  RadioButtonModule,
  SelectButtonModule,
  TabViewModule,
  TableModule,
  ToastModule,
  TooltipModule
];


/**
 * A factory to load the translations and configuration before the
 * application is created.  This allows us to immediately use the
 * translations and config without it needing to be async.
 * 
 * @param trans - translation service
 * @param config - configuration service
 * @returns an observable that angular waits on to complete.
 */

export function initializeAppFactory(
  trans: TranslateService,
  env: ScrEnvironment,
  config: ConfigService,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logger: NGXLogger): () => Observable<any> {

  let logLevel = NgxLoggerLevel.DEBUG;
  const lvl = localStorage.getItem('loglevel');
  const num = lvl ? parseInt(lvl, 10) : NgxLoggerLevel.DEBUG;
  if (!Number.isNaN(num)) {
    logLevel = num;
  }
  logger.partialUpdateConfig({ level: logLevel, enableSourceMaps: true });

  // load translations
  const lang = 'en';
  trans.defaultLang = lang;
  trans.use(lang);

  let tenantName = env.stage;
  if (tenantName === 'scr-devops') {
    tenantName = 'engdevelopment';
  }

  return () => forkJoin([
    trans.getTranslation(lang).pipe(take(1), tap(() => {
      // this.logger.trace(`${lang} translations loaded`);
    })),
    config.getConfig().pipe(take(1), tap(() => {
      // this.logger.trace('config loaded');
    }))
  ]);

}

